// API base URL - defaults to same origin in production, or localhost in development
const getApiBaseUrl = (): string => {
  const envUrl = import.meta.env.VITE_API_URL;
  if (envUrl) return envUrl;

  // In production, use same origin
  if (import.meta.env.PROD) {
    return '';
  }

  // Default to localhost for development
  return 'http://localhost:8000';
};

export const API_BASE_URL = getApiBaseUrl();

// Custom error class for API errors
export class ApiError extends Error {
  status: number;
  bodyText?: string;

  constructor(message: string, status: number, bodyText?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.bodyText = bodyText;
  }
}

// Type for Clerk's getToken function
type GetToken = () => Promise<string | null>;

type ApiFetchInit = Omit<RequestInit, 'headers'> & {
  headers?: Record<string, string>;
  auth?: boolean;
};

// Fetch with optional auth token
export async function apiFetch(
  getToken: GetToken,
  path: string,
  init?: ApiFetchInit
): Promise<Response> {
  const url = `${API_BASE_URL}${path}`;
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(init?.headers ?? {}),
  };

  const auth = init?.auth ?? true;
  if (auth) {
    const token = await getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  return fetch(url, {
    ...init,
    headers,
  });
}

// Fetch and parse JSON with auth
export async function apiJson<T>(
  getToken: GetToken,
  path: string,
  init?: ApiFetchInit
): Promise<T> {
  const res = await apiFetch(getToken, path, init);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ApiError(
      `Request failed: ${res.status} ${res.statusText}`,
      res.status,
      text
    );
  }

  // 204 No Content
  if (res.status === 204) return undefined as unknown as T;

  return (await res.json()) as T;
}

// Legacy function for non-authenticated requests
export async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }

  return response.json();
}
